import { useState, MouseEvent as ReactMouseEvent } from "react"

import { Popover } from "@material-ui/core"
import { css, cx } from "@emotion/css"
import { useI18n } from "providers/I18n"

type Props<T> = {
  data?: T[]
  label: string
  name: string
  slice?: number
  className?: string
}

export function MorePopover<T extends Record<string, any>>(props: Props<T>) {
  const { data, label, name, slice = 1, className } = props
  const { t } = useI18n()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const popover = Boolean(anchorEl)
  const handlePopoverOpen = (
    event: ReactMouseEvent<HTMLElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      {data?.slice(slice)?.length ? (
        <span
          aria-owns={popover ? "mouse-over-popover" : undefined}
          aria-haspopup="true"
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          className={cx(styles.hover, className)}
        >
          {`+${data?.slice(slice)?.length}
              ${t(`${label}.more`)}`}
        </span>
      ) : null}
      <Popover
        id="mouse-over-popover"
        className={styles.popover}
        classes={{
          paper: styles.paper,
        }}
        open={popover}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          onMouseEnter: handlePopoverOpen,
          onMouseLeave: handlePopoverClose,
        }}
        disableRestoreFocus
      >
        {data?.slice(slice)?.map((d, idx: number) => (
          <span className={styles.popoverList} key={idx}>
            {d[name]}
          </span>
        ))}
      </Popover>
    </>
  )
}

const styles = {
  hover: css`
    color: var(--primary-main);
    cursor: pointer;
    font-size: 13px;
    margin-left: 4px;
    margin-right: var(--spacing-m);
    white-space: nowrap;
  `,
  popover: css`
    pointer-events: none;
  `,
  paper: css`
    padding: 1rem;
    display: flex;
    flex-direction: column;
    text-transform: capitalize;
    pointer-events: auto;
    > span:not(:last-child) {
      padding-bottom: var(--spacing-s);
    }
  `,
  popoverList: css`
    font-size: 13px;
    width: max-content;
    max-width: 600px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
}
